"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Dumbbell, Scale, Apple, Ruler } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { resolveHomeRouteForUser } from "@/lib/auth/home-route-resolution";
import { AppStartupScreen } from "@/components/startup/app-startup-screen";
import {
  isNativeSessionBridgeRequired,
  syncNativeSessionToServer,
} from "@/lib/supabase/session-bridge";

export default function Home() {
  const router = useRouter();
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      if (cancelled) return;
      setIsChecking(false);
    }, 10000);

    async function checkAuthAndRedirect() {
      const supabase = createClient();

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;

        if (session?.user) {
          if (isNativeSessionBridgeRequired()) {
            const syncResult = await syncNativeSessionToServer(session, "landing-check-existing-session");
            if (cancelled) return;
            if (!syncResult.ok) {
              if (syncResult.status === 401) {
                await supabase.auth.signOut();
              }
              setIsChecking(false);
              return;
            }
          }

          const role = typeof session.user.user_metadata?.role === "string"
            ? session.user.user_metadata.role
            : null;
          const targetRoute = await resolveHomeRouteForUser(supabase, {
            userId: session.user.id,
            role,
          });
          if (cancelled) return;
          if (pathnameRef.current !== "/") return;
          router.replace(targetRoute);
          return;
        }
      } catch (error) {
        console.error("Auth check error:", error);
      }
      if (!cancelled) setIsChecking(false);
    }

    checkAuthAndRedirect();
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [router]);

  if (isChecking) {
    return <AppStartupScreen />;
  }

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-center bg-[#090c12] px-4 py-6 md:py-12">
      <div className="text-center max-w-3xl">
        <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-slate-100 mb-1 md:mb-4 tracking-tight">
          For Athletes.
        </h1>
        <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-emerald-400 mb-4 md:mb-8 tracking-tight">
          By Athletes.
        </h1>

        <p className="text-base md:text-xl text-slate-400 mb-6 md:mb-10 font-medium tracking-wide">
          Workouts &bull; Weight &bull; Nutrition &bull; Measurements
        </p>

        <div className="flex gap-3 md:gap-4 justify-center mb-8 md:mb-16">
          <Link href="/signup" prefetch={true}>
            <Button
              size="lg"
              className="bg-slate-100 text-slate-900 px-6 py-5 md:px-8 md:py-6 rounded-full font-semibold text-base md:text-lg hover:bg-white"
            >
              Get Started
            </Button>
          </Link>
          <Link href="/login" prefetch={true}>
            <Button
              variant="outline"
              size="lg"
              className="border-2 border-slate-700 bg-transparent text-slate-200 px-6 py-5 md:px-8 md:py-6 rounded-full font-semibold text-base md:text-lg hover:border-slate-500 hover:bg-slate-900"
            >
              Sign In
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-4 gap-2 md:gap-6 text-center">
          <div className="p-2 md:p-6">
            <div className="w-10 h-10 md:w-14 md:h-14 bg-blue-500/15 rounded-xl md:rounded-2xl mx-auto mb-2 md:mb-4 flex items-center justify-center border border-blue-400/30">
              <Dumbbell className="w-5 h-5 md:w-7 md:h-7 text-blue-300" />
            </div>
            <h3 className="font-bold text-slate-100 text-xs md:text-base">Workouts</h3>
            <p className="text-slate-400 text-[10px] md:text-sm mt-0.5 md:mt-1 hidden sm:block">Log sets, reps & PRs</p>
          </div>
          <div className="p-2 md:p-6">
            <div className="w-10 h-10 md:w-14 md:h-14 bg-emerald-500/15 rounded-xl md:rounded-2xl mx-auto mb-2 md:mb-4 flex items-center justify-center border border-emerald-400/30">
              <Scale className="w-5 h-5 md:w-7 md:h-7 text-emerald-300" />
            </div>
            <h3 className="font-bold text-slate-100 text-xs md:text-base">Weight</h3>
            <p className="text-slate-400 text-[10px] md:text-sm mt-0.5 md:mt-1 hidden sm:block">Track trends & goals</p>
          </div>
          <div className="p-2 md:p-6">
            <div className="w-10 h-10 md:w-14 md:h-14 bg-orange-500/15 rounded-xl md:rounded-2xl mx-auto mb-2 md:mb-4 flex items-center justify-center border border-orange-400/30">
              <Apple className="w-5 h-5 md:w-7 md:h-7 text-orange-300" />
            </div>
            <h3 className="font-bold text-slate-100 text-xs md:text-base">Nutrition</h3>
            <p className="text-slate-400 text-[10px] md:text-sm mt-0.5 md:mt-1 hidden sm:block">Meals, macros, calories</p>
          </div>
          <div className="p-2 md:p-6">
            <div className="w-10 h-10 md:w-14 md:h-14 bg-violet-500/15 rounded-xl md:rounded-2xl mx-auto mb-2 md:mb-4 flex items-center justify-center border border-violet-400/30">
              <Ruler className="w-5 h-5 md:w-7 md:h-7 text-violet-300" />
            </div>
            <h3 className="font-bold text-slate-100 text-xs md:text-base">Measurements</h3>
            <p className="text-slate-400 text-[10px] md:text-sm mt-0.5 md:mt-1 hidden sm:block">Body stats over time</p>
          </div>
        </div>
      </div>
    </div>
  );
}
